<template>
  <!-- 手机浏览器打开 dashboard 时顶部弹出「是否打开 App」。
       原来在 views/MobileNotice.vue 里，和「屏幕太小」全屏拦截页绑在一起，2026-09-20（91a32d84）
       手机端能正常用之后拦截页被整个删掉，这个提示也跟着没了。这里只还原提示，不拦页面。 -->
  <transition name="open-app-slide">
    <div v-if="visible" class="open-app-card" role="dialog" :aria-label="$t('login.Open_in_app_title')">
      <img src="@/assets/login-logo.png" alt="" class="open-app-card__icon" />
      <div class="open-app-card__text">
        <div class="open-app-card__title">{{ $t('login.Open_in_app_title') }}</div>
        <div class="open-app-card__subtitle">{{ $t('login.Open_in_app_tip') }}</div>
      </div>
      <div class="open-app-card__actions">
        <button type="button" class="open-app-card__btn open-app-card__btn--cancel" @click="dismiss">{{ $t('login.Cancel') }}</button>
        <button type="button" class="open-app-card__btn" @click="openApp">{{ $t('login.Open_in_app_button') }}</button>
      </div>
    </div>
  </transition>
</template>

<script>
// 点过「取消」或「打开 App」后，本次浏览器会话内不再弹（关掉标签页重新打开会再问一次）
const DISMISS_KEY = "wtk:openAppPromptDismissed";

function wasDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export default {
  name: "OpenAppPrompt",
  data() {
    return {
      // 只看是不是手机（iPhone / iPod / Android），和原 MobileNotice 一致；平板、桌面窄窗口都不弹
      visible: /iPhone|iPod|Android/i.test(navigator.userAgent) && !wasDismissed(),
    };
  },
  methods: {
    dismiss() {
      this.visible = false;
      try {
        window.sessionStorage.setItem(DISMISS_KEY, "1");
      } catch (e) {
        // 隐私模式下存不了，只是下次刷新会再弹一次
      }
    },
    // 从当前路由 query 中取出 module / hotelId / restaurantId / periodType / periodStart / periodEnd，
    // 一并带给 App，让 App 能定位到具体是哪个酒店 / 餐厅、哪个报表周期。
    buildExtraQuery(query) {
      const extra = new URLSearchParams();
      ["module", "hotelId", "restaurantId", "periodType", "periodStart", "periodEnd"].forEach((key) => {
        if (query[key]) extra.set(key, query[key]);
      });
      return extra.toString();
    },
    // 未登录时全局路由守卫会把原目标塞进 redirect 参数跳到 /login?redirect=xxx，
    // 这时真正的目标在 redirect 字符串里，而不是当前路由上。
    resolveTarget() {
      const path = this.$route.path || "";
      const query = this.$route.query || {};
      if (path.startsWith("/login") && query.redirect) {
        const [redirectPath, redirectQueryStr] = String(query.redirect).split("?");
        const redirectQuery = redirectQueryStr ? Object.fromEntries(new URLSearchParams(redirectQueryStr)) : {};
        return { path: redirectPath || "", query: redirectQuery };
      }
      return { path, query };
    },
    // 带 reviewId 的 Case Management / Reviews 链接跳到 App 的评论详情，Reports 跳报表页，其余跳 App 登录页
    buildAppScheme() {
      const { path, query } = this.resolveTarget();
      const isReviewPage = path.startsWith("/NeedsAttention") || path.startsWith("/reviews") || path.startsWith("/ReplySettingIndex");
      const reviewId = isReviewPage ? query.reviewId || "" : "";
      let scheme = "wheretoknow://login";
      if (reviewId) {
        scheme = `wheretoknow://review/${encodeURIComponent(reviewId)}`;
      } else if (path.startsWith("/Reports")) {
        scheme = "wheretoknow://reports";
      }
      if (scheme !== "wheretoknow://login") {
        const extraQuery = this.buildExtraQuery(query);
        if (extraQuery) scheme += `?${extraQuery}`;
      }
      return scheme;
    },
    openApp() {
      const scheme = this.buildAppScheme();
      this.dismiss();
      window.location.href = scheme;
    },
  },
};
</script>

<style scoped>
.open-app-card {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* 盖在手机顶栏（Navbar 1011）和侧边栏抽屉（1045）之上，低于 Element 弹层起始值 2000 */
  z-index: 1990;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  padding: 16px 20px;
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  font-family: Inter, sans-serif;
}

.open-app-card__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  object-fit: contain;
}

.open-app-card__text {
  flex: 1;
  min-width: 0;
}

.open-app-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #232323;
  line-height: 1.4;
}

.open-app-card__subtitle {
  font-size: 13px;
  color: #6a6969;
  margin-top: 2px;
  line-height: 1.4;
}

.open-app-card__actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.open-app-card__btn {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #2563eb;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px;
  white-space: nowrap;
}

.open-app-card__btn--cancel {
  color: #6a6969;
}

.open-app-slide-enter-active,
.open-app-slide-leave-active {
  transition: transform 0.2s ease;
}
.open-app-slide-enter,
.open-app-slide-leave-to {
  transform: translateY(-100%);
}
</style>
